// The goal and the wins in the Masterminds section's member view (docs/mastermind-admin-plan.md 3.3), fed
// from the staff version of hub.accountability_log, which holds the goal with its agreed and reached days
// and its agreed versions, and every win with who logged it. Owner and admin change them: the goal through
// the staff definers (lock_goal with the day it was agreed, unlock_goal, mark_goal_reached), the wins as
// direct writes under row level security. Every change is kept in the history of changes (triggers) and
// the staff ones are audited.
import { useId, useMemo, useState } from 'react'
import { Button, Card, Checkbox, ConfirmPanel, DateField, Field, Select, TextArea, TextInput, todayInUk, useAdmin } from '../ui/index.js'
import { dmy } from '../../products/mastermind/log.js'
import { LIMITS } from '../../products/accelerator/limits.js'
import { MM, PANEL, day } from './words.js'
import { lockGoal, unlockGoal, markReached, addWin, saveWin, deleteWin } from './data.js'
import { Said } from './ThisMonth.jsx'
import '../../styles/admin-mastermind.css'

const CATEGORIES = ['deal', 'finance', 'income', 'marketing', 'milestone']

// ---- The goal -------------------------------------------------------------------------------------------

export function GoalSection({ log, person, first, product, oa, ctx, onChanged }) {
  const { api } = useAdmin()
  const goal = log.goal
  const locked = goal?.status === 'locked'
  const today = todayInUk()
  const joined = log.access?.start_day || null
  const [text, setText] = useState(goal?.body || '')
  const [agreedOn, setAgreedOn] = useState(today)
  const [reaching, setReaching] = useState(false)
  const [reachedOn, setReachedOn] = useState(today)
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)
  const ids = useId()
  const versions = goal?.versions || []
  const changed = versions.filter(v => v.changed_on).slice(-1)[0]

  async function run(fn, done) {
    if (busy) return false
    setBusy(true); setLine(null)
    try {
      await fn()
      await onChanged()
      setLine({ text: done })
      return true
    } catch (e) {
      setLine({ bad: true, text: e.message })
      return false
    } finally {
      setBusy(false)
    }
  }
  const lock = () => run(() => lockGoal(api, { person, product, body: text, agreedOn: goal?.agreed_on ? null : agreedOn }, { ...ctx, dateLine: MM.agreedBadDate }), PANEL.locked(first))
  const unlock = () => run(() => unlockGoal(api, { person, product }, ctx), PANEL.unlocked(first))
  const reach = async () => { if (await run(() => markReached(api, { person, product, on: reachedOn }, { ...ctx, dateLine: MM.reachedBadDate }), PANEL.reachedDone)) setReaching(false) }
  const undo = () => run(() => markReached(api, { person, product, on: null }, ctx), PANEL.undone)
  const tooLong = text.trim().length > LIMITS.goal
  const meta = locked
    ? [PANEL.goalAgreed(dmy(goal.agreed_on || goal.locked_at)), changed ? PANEL.goalChanged(dmy(changed.changed_on)) : null].filter(Boolean).join(' ')
    : goal ? PANEL.goalDraft(first) : PANEL.goalNone
  const hint = PANEL.goalHint(first) + ' ' + PANEL.goalCount(text.trim().length) + '.'

  return (
    <Card id="mm-goal" title={PANEL.goal} className="amx-goal">
      {goal && (locked || !oa) && <p className="amx-goal-body">{goal.body}</p>}
      <p className="amx-meta">{meta}</p>
      {goal?.reached_on && (
        <p className="amx-meta amx-strong">{PANEL.reachedLine(dmy(goal.reached_on))} {oa && <Button size="xs" busy={busy} onClick={undo}>{PANEL.undo}</Button>}</p>
      )}
      {oa && locked && (
        <div className="amx-row-actions">
          <Button size="sm" busy={busy} onClick={unlock}>{PANEL.unlock}</Button>
          {!goal.reached_on && !reaching && <Button size="sm" onClick={() => { setReaching(true); setLine(null) }}>{PANEL.reach}</Button>}
        </div>
      )}
      {oa && locked && reaching && (
        <div className="amx-form amx-inline" role="group" aria-label={PANEL.reach}>
          <DateField label={PANEL.reachedOn} value={reachedOn} onChange={setReachedOn} min={goal.agreed_on || undefined} max={today} plain />
          <span className="amx-row-actions">
            <Button kind="ink" size="sm" busy={busy} disabled={!reachedOn} onClick={reach}>{PANEL.reachSave}</Button>
            <Button size="sm" disabled={busy} onClick={() => setReaching(false)}>{PANEL.cancel}</Button>
          </span>
        </div>
      )}
      {oa && !locked && (
        <div className="amx-form">
          <Field label={PANEL.goalLabel} plain hint={hint} error={tooLong ? PANEL.goalTooLong : null}>
            {props => <TextArea {...props} value={text} onChange={v => { setText(v); setLine(null) }} rows={3} placeholder={PANEL.goalPlaceholder(first)} />}
          </Field>
          {goal?.agreed_on
            ? <p className="amx-muted">{PANEL.agreedKept(dmy(goal.agreed_on))}</p>
            : <DateField label={PANEL.agreedOn} value={agreedOn} onChange={setAgreedOn} min={joined || undefined} max={today} hint={PANEL.agreedOnHint} plain />}
          <span><Button kind="ink" size="sm" busy={busy} disabled={!text.trim() || tooLong || (!goal?.agreed_on && !agreedOn)} onClick={lock}>{PANEL.lock}</Button></span>
        </div>
      )}
      {versions.length > 0 && (
        <details className="amx-versions">
          <summary id={`${ids}-v`}>{PANEL.versions(versions.length)}</summary>
          <ol className="amx-version-list" aria-labelledby={`${ids}-v`}>
            {versions.map((v, i) => (
              <li key={`${v.at}-${i}`}>
                <span className="amx-muted">{PANEL.versionLine(v.agreed_on ? dmy(v.agreed_on) : '', v.changed_on ? dmy(v.changed_on) : null)}</span>
                <span className="amx-version-body">{v.body}</span>
              </li>
            ))}
          </ol>
        </details>
      )}
      <Said line={line} />
    </Card>
  )
}

// ---- Wins -----------------------------------------------------------------------------------------------

export function WinsSection({ log, person, first, product, oa, ctx, onChanged }) {
  const { api } = useAdmin()
  const wins = useMemo(() => [...(log.wins || [])].sort((a, b) => String(b.win_date).localeCompare(String(a.win_date))), [log.wins])
  const [form, setForm] = useState(null) // 'new' or a win id
  const [deleting, setDeleting] = useState(null)
  const [busy, setBusy] = useState(false)
  const [line, setLine] = useState(null)
  const shared = wins.filter(w => w.shareable).length

  async function remove(w) {
    setBusy(true)
    try {
      await deleteWin(api, w.win_id, ctx)
      setDeleting(null)
      await onChanged()
      setLine({ text: PANEL.winDeleted })
    } catch (e) {
      setLine({ bad: true, text: e.message })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card id="mm-wins" title={PANEL.wins} aside={wins.length > 0 ? PANEL.winsCount(wins.length, shared) : null} className="amx-wins">
      {!wins.length && <p className="amm-muted">{PANEL.noWins(first)}</p>}
      {wins.length > 0 && (
        <ul className="amm-wins">
          {wins.map(w => (
            <li key={w.win_id} className="amm-win">
              {form === w.win_id
                ? <WinForm win={w} person={person} first={first} product={product} ctx={ctx}
                    onCancel={() => setForm(null)} onSaved={async () => { setForm(null); await onChanged(); setLine({ text: PANEL.winSaved }) }} />
                : (
                  <>
                    <span className="amm-win-title">{w.title}</span>
                    <span className="amm-win-line">
                      <span>{PANEL.winLine(w.category, day(w.win_date))}</span>
                      <span className="ad-tag">{!w.shareable ? PANEL.privateTag : w.logged_by === 'member' ? PANEL.shareTagMember(first) : PANEL.shareTag}</span>
                      <span className="amm-muted">{PANEL.winLoggedBy(w.logged_by)}</span>
                    </span>
                    {w.detail && <span className="amm-win-detail">{w.detail}</span>}
                    {oa && deleting !== w.win_id && (
                      <span className="amm-row-actions">
                        <Button size="xs" onClick={() => { setForm(w.win_id); setLine(null) }} aria-label={`${PANEL.edit}: ${w.title}`}>{PANEL.edit}</Button>
                        <Button size="xs" onClick={() => { setDeleting(w.win_id); setLine(null) }} aria-label={`${PANEL.deleteWin}: ${w.title}`}>{PANEL.deleteWin}</Button>
                      </span>
                    )}
                    {deleting === w.win_id && (
                      <ConfirmPanel text={PANEL.deleteConfirm(w.title)} confirmLabel={PANEL.deleteYes} cancelLabel={PANEL.keep}
                        busy={busy} onConfirm={() => remove(w)} onCancel={() => setDeleting(null)} />
                    )}
                  </>
                )}
            </li>
          ))}
        </ul>
      )}
      <Said line={line} />
      {oa && (form === 'new'
        ? <WinForm person={person} first={first} product={product} ctx={ctx}
            onCancel={() => setForm(null)} onSaved={async () => { setForm(null); await onChanged(); setLine({ text: PANEL.winAdded }) }} />
        : <span><Button size="sm" onClick={() => { setForm('new'); setLine(null) }}>{PANEL.addWin}</Button></span>)}
    </Card>
  )
}

function WinForm({ win, person, first, product, ctx, onCancel, onSaved }) {
  const { api } = useAdmin()
  const [title, setTitle] = useState(win?.title || '')
  const [category, setCategory] = useState(win?.category || '')
  const [date, setDate] = useState(win?.win_date || todayInUk())
  const [detail, setDetail] = useState(win?.detail || '')
  const [share, setShare] = useState(Boolean(win?.shareable))
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState(null)
  const ready = title.trim() && title.trim().length <= LIMITS.winTitle && category
  // a saved win, whoever logged it: whether it may be shared is the member's answer, shown, never changed
  // here. Since Adam's answer 6 the member changes it on every win of theirs, so staff record a yes only
  // when they log a win (private.guard_wins, migration 20261007110000: 42501 share_member_only)
  const theirs = Boolean(win)

  async function go() {
    if (busy) return
    if (!ready) { setProblem(PANEL.winTitleBad); return }
    setBusy(true); setProblem(null)
    try {
      const body = { title, category, winDate: date, detail, shareable: share }
      // the share flag goes only with a new win, never with a saved win
      if (theirs) delete body.shareable
      if (win) await saveWin(api, { id: win.win_id, ...body }, ctx)
      else await addWin(api, { person, product, ...body }, ctx)
      await onSaved()
    } catch (e) {
      setProblem(e.message)
      setBusy(false)
    }
  }
  return (
    <div className="amm-form amm-winform" role="group" aria-label={win ? `${PANEL.edit}: ${win.title}` : PANEL.addWin}>
      <Field label={PANEL.winTitle} plain>
        {props => <TextInput {...props} value={title} onChange={v => { setTitle(v); setProblem(null) }} maxLength={LIMITS.winTitle} placeholder={PANEL.winTitlePlaceholder} />}
      </Field>
      <div className="amm-form-pair">
        <Field label={PANEL.winCategory} plain>
          {props => <Select {...props} value={category} onChange={setCategory} placeholder="" options={CATEGORIES.map(c => ({ value: c, label: PANEL.categories[c] }))} />}
        </Field>
        <DateField label={PANEL.winDate} value={date} onChange={setDate} max={todayInUk()} plain />
      </div>
      <Field label={PANEL.winDetail} plain>
        {props => <TextArea {...props} value={detail} onChange={setDetail} rows={2} maxLength={LIMITS.winDetail} placeholder={PANEL.winDetailPlaceholder} />}
      </Field>
      {theirs
        ? <p className="amm-muted amm-share-theirs">{PANEL.winShareMember(first, Boolean(win.shareable))}</p>
        : <Checkbox checked={share} onChange={setShare}>{PANEL.winShare(first)}</Checkbox>}
      <span className="amm-row-actions">
        <Button kind="ink" size="sm" busy={busy} disabled={!ready} onClick={go}>{PANEL.saveWin}</Button>
        <Button size="sm" onClick={onCancel} disabled={busy}>{PANEL.cancel}</Button>
      </span>
      {problem && <p className="ad-error" role="alert">{problem}</p>}
    </div>
  )
}

